import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalBody } from "reactstrap";
import ConfirmModal from "../../Components/Common/ConfirmModal";
import { AppDispatch } from "../../store";
import { showAlertWithTimeout } from "../../slices/Alert/reducer";
import { resolveApiMessage } from "../../helpers/apiMessage";
import { createZone, deleteZone, getZones, updateZone } from "../../slices/Zones/thunk";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import HomeLogoLink from "../../Components/Common/HomeLogoLink";

const getZoneId = (zone: any): string | number | undefined => {
    if (!zone || typeof zone !== "object") {
        return undefined;
    }

    const candidateKeys = ["id", "uuid", "_id", "zoneId", "slug"];

    for (const key of candidateKeys) {
        const value = (zone as Record<string, any>)[key];
        if (value !== undefined && value !== null && value !== "") {
            return value as string | number;
        }
    }

    return undefined;
};

const getZoneName = (zone: any): string => {
    if (!zone || typeof zone !== "object") {
        return "Chưa đặt tên khu vực";
    }

    const candidateKeys = ["name", "label", "title"];

    for (const key of candidateKeys) {
        const value = (zone as Record<string, any>)[key];
        if (typeof value === "string" && value.trim().length > 0) {
            return value.trim();
        }
    }

    return "Chưa đặt tên khu vực";
};

const ShopZones = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { zones, loading, error } = useSelector((state: any) => state.Zones);
    const zonesList = useMemo(() => (Array.isArray(zones) ? zones : []), [zones]);

    const [showAddZoneModal, setShowAddZoneModal] = useState(false);
    const [newZoneName, setNewZoneName] = useState("");
    const [zoneNameError, setZoneNameError] = useState("");
    const ZONE_NAME_MAX_LENGTH = 100;

    const openAddZoneModal = () => {
        setNewZoneName("");
        setZoneNameError("");
        setShowAddZoneModal(true);
    };

    const closeAddZoneModal = () => {
        setShowAddZoneModal(false);
        setNewZoneName("");
        setZoneNameError("");
    };

    const [showDeleteZoneModal, setShowDeleteZoneModal] = useState(false);
    const [deleteZoneId, setDeleteZoneId] = useState<string | number | null>(null);
    const [showEditZoneModal, setShowEditZoneModal] = useState(false);
    const [editZoneId, setEditZoneId] = useState<string | number | null>(null);
    const [editZoneName, setEditZoneName] = useState("");
    const [editZoneNameError, setEditZoneNameError] = useState("");

    useEffect(() => {
        dispatch(getZones());
    }, [dispatch]);

    const closeEditZoneModal = () => {
        setShowEditZoneModal(false);
        setEditZoneId(null);
        setEditZoneName("");
        setEditZoneNameError("");
    };

    const openEditZoneModal = (zone: any) => {
        const identifier = getZoneId(zone);
        setEditZoneId(identifier ?? null);
        setEditZoneName(getZoneName(zone));
        setEditZoneNameError("");
        setShowEditZoneModal(true);
    };

    const handleDeleteZone = async () => {
        if (!deleteZoneId) {
            return;
        }

        try {
            const deleteResponse = await dispatch(deleteZone(deleteZoneId)).unwrap();
            dispatch(showAlertWithTimeout("success", "Thành công", resolveApiMessage(deleteResponse, { status: 200 })));
            setDeleteZoneId(null);
            dispatch(getZones());
        } catch (deleteError) {
            dispatch(
                showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(deleteError))
            );
        }
    };

    const handleAddZone = async () => {
        const trimmedName = newZoneName.trim();

        if (trimmedName.length === 0) {
            setZoneNameError("Vui lòng nhập tên khu vực");
            return;
        }

        if (trimmedName.length > ZONE_NAME_MAX_LENGTH) {
            setZoneNameError(`Tên khu vực không được vượt quá ${ZONE_NAME_MAX_LENGTH} ký tự`);
            return;
        }

        try {
            const response = await dispatch(createZone({ name: trimmedName })).unwrap();
            closeAddZoneModal();
            dispatch(getZones());
            dispatch(showAlertWithTimeout("success", "Thành công", resolveApiMessage(response, { status: 200 })));
        } catch (createError) {
            setZoneNameError(resolveApiMessage(createError));
            dispatch(
                showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(createError))
            );
        }
    };

    const handleEditZone = async () => {
        if (!editZoneId) {
            setEditZoneNameError("Không tìm thấy khu vực cần sửa.");
            return;
        }

        const trimmedName = editZoneName.trim();

        if (trimmedName.length === 0) {
            setEditZoneNameError("Vui lòng nhập tên khu vực");
            return;
        }

        if (trimmedName.length > ZONE_NAME_MAX_LENGTH) {
            setEditZoneNameError(
                `Tên khu vực không được vượt quá ${ZONE_NAME_MAX_LENGTH} ký tự`
            );
            return;
        }

        try {
            const response = await dispatch(updateZone({ zoneId: editZoneId, data: { name: trimmedName } })).unwrap();
            closeEditZoneModal();
            dispatch(getZones());
            dispatch(showAlertWithTimeout("success", "Thành công", resolveApiMessage(response, { status: 200 })));
        } catch (updateError) {
            setEditZoneNameError(resolveApiMessage(updateError));
            dispatch(
                showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(updateError))
            );
        }
    };

    const renderZoneContent = () => {
        if (loading) {
            return (
                <div className="cart-table card mb-3">
                    <div className="table-responsive card-body">
                        <table className="table mb-0">
                            <tbody>
                                <tr>
                                    <td colSpan={2} className="text-center py-3">
                                        Đang tải danh sách khu vực...
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }

        if (zonesList.length === 0) {
            return (
                <div className="cart-table card mb-3">
                    <div className="table-responsive card-body">
                        <table className="table mb-0">
                            <tbody>
                                <tr>
                                    <td colSpan={2} className="text-center py-3">
                                        {error ?? "Chưa có khu vực nào."}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }

        return zonesList.map((item: any, index: number) => {
            const zoneId = getZoneId(item);
            const zoneName = getZoneName(item);

            return (
                <div className="cart-table card mb-3" key={zoneId ?? index}>
                    <div className="table-responsive card-body">
                        <table className="table mb-0">
                            <tbody>
                                <tr>
                                    <td>
                                        <Link className="product-title" to={`/shop/zones/${zoneId ?? ""}`}>
                                            Khu vực {zoneName}
                                        </Link>
                                    </td>
                                    <td className="text-end align-middle">
                                        <Link
                                            className="btn btn-info btn-sm text-white me-1"
                                            to={`/shop/zones/${zoneId ?? ""}`}
                                            title="Xem danh sách bàn"
                                            aria-label={`Xem danh sách bàn khu vực ${zoneName}`}
                                        >
                                            <i className="mdi mdi-table-chair"></i>
                                        </Link>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm text-white me-1"
                                            onClick={() => {
                                                openEditZoneModal(item);
                                            }}
                                        >
                                            <i className="mdi mdi-lead-pencil"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm text-white"
                                            onClick={() => {
                                                setDeleteZoneId(zoneId ?? null);
                                                setShowDeleteZoneModal(true);
                                            }}
                                        >
                                            <i className="mdi mdi-delete"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        });
    };

    return (
        <React.Fragment>
            <ConfirmModal
                isOpen={showDeleteZoneModal}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content="Bạn có chắc chắn muốn xóa khu vực này không?"
                onConfirm={() => {
                    setShowDeleteZoneModal(false);
                    handleDeleteZone();
                }}
                onCancel={() => {
                    setShowDeleteZoneModal(false);
                    setDeleteZoneId(null);
                }}
            />
            <Modal size="sm" isOpen={showAddZoneModal} toggle={closeAddZoneModal} centered>
                <ModalBody>
                    <h6 className="fs-15">Vui lòng nhập tên khu vực</h6>
                    <div className="mb-3">
                        <input
                            className="form-control"
                            type="text"
                            id="zone-name"
                            placeholder="VD: Ngoài trời, Tầng 1, Tầng 2, ..."
                            value={newZoneName}
                            maxLength={ZONE_NAME_MAX_LENGTH}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setNewZoneName(event.target.value);
                                if (zoneNameError) {
                                    setZoneNameError("");
                                }
                            }}
                        />
                        {zoneNameError && (
                            <div className="invalid-feedback d-block">{zoneNameError}</div>
                        )}
                    </div>
                </ModalBody>
                <div className="modal-footer">
                    <Button color="secondary" onClick={closeAddZoneModal}>
                        Hủy
                    </Button>
                    <Button color="primary" onClick={handleAddZone}>
                        Thêm
                    </Button>
                </div>
            </Modal>

            <Modal size="sm" isOpen={showEditZoneModal} toggle={closeEditZoneModal} centered>
                <ModalBody>
                    <h6 className="fs-15">Chỉnh sửa tên khu vực</h6>
                    <div className="mb-3">
                        <input
                            className="form-control"
                            type="text"
                            placeholder="VD: Ngoài trời, Tầng 1, Tầng 2, ..."
                            value={editZoneName}
                            maxLength={ZONE_NAME_MAX_LENGTH}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setEditZoneName(event.target.value);
                                if (editZoneNameError) {
                                    setEditZoneNameError("");
                                }
                            }}
                        />
                        {editZoneNameError && (
                            <div className="invalid-feedback d-block">{editZoneNameError}</div>
                        )}
                    </div>
                </ModalBody>
                <div className="modal-footer">
                    <Button color="secondary" onClick={closeEditZoneModal}>
                        Hủy
                    </Button>
                    <Button color="primary" onClick={handleEditZone}>
                        Lưu
                    </Button>
                </div>
            </Modal>

            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />

                    <div className="page-heading">
                        <h6 className="mb-0">Khu vực &amp; bàn</h6>
                    </div>

                    <div className="d-flex align-items-center">
                        <div
                            className="filter-option"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#suhaFilterOffcanvas"
                            aria-controls="suhaFilterOffcanvas"
                        >
                            <i onClick={openAddZoneModal} className="mdi mdi-plus"></i>
                        </div>
                        <HeaderSidebarToggle className="ms-2" />
                    </div>
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="cart-wrapper-area py-3">{renderZoneContent()}</div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ShopZones;
